import React, { useEffect, useMemo, useState } from 'react';
import LegacyPratusTable from 'components/DesignSystems/PratusTable/LegacyPratusTable';
import RosterExpandedRow from './RosterExpandedRow';
import RosterResourceModal from './RosterResourceModal';
import { RosterTableProps } from './types';
import {
  useGetIncidentLogs,
  IncidentLogEntry,
} from 'hooks/incident/useWorkLog';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  IncidentPosition,
  SeatResource,
  useGetPositionsForIncident,
  useGetSeatResourcesForRoster,
} from 'hooks/incident/useRoster';
import { resolveCategoryLabel } from '../positionCategories';
import { rosterEntryMatchesSearch } from './rosterPositionFilter';
import { Badge } from '@/components/ui/badge';
import { useHwcgFlag } from 'utils/featureFlags';
import { useHwcgActivationLog } from 'hooks/useHwcgActivationLog';
import { HwcgActivationSummary } from './activationLog/HwcgActivationSummary';
import {
  formatLogTime,
  indexUsersByGuid,
  latestActivationState,
} from './activationLog/hwcgActivationLogModel';
// Constants moved outside component
const TOGGLE_DATA_CHECK_IN = [
  { label: 'Checked-In', value: 'Checked-In' },
  { label: 'Checked Out', value: 'Checked Out' },
];

const TOGGLE_DATA_SIGN_IN = [
  { label: 'Signed-In', value: 'Signed-In' },
  { label: 'Signed-Out', value: 'Signed-Out' },
];

const formatRosterTimestamp = (
  iso: string | null | undefined
): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
};

const StatusWithSubline: React.FC<{
  status: React.ReactNode;
  timestampIso: string | null | undefined;
}> = ({ status, timestampIso }) => {
  const formatted = formatRosterTimestamp(timestampIso);
  return (
    <div>
      <div>{status}</div>
      {formatted ? (
        <small className="text-muted d-block">{formatted}</small>
      ) : null}
    </div>
  );
};

const getLatestActiveSession = (
  userGuid: string,
  incidentLogs?: IncidentLogEntry[]
) => {
  if (!incidentLogs) return null;

  const userLog = incidentLogs.find((log) => log.userGuid === userGuid);
  if (!userLog?.logs || userLog.logs.length === 0) return null;

  const allSessions = userLog.logs.flatMap((log) => log.sessions || []);

  const sortedSessions = allSessions
    .filter((session) => session.signInTime)
    .sort(
      (a, b) =>
        new Date(b.signInTime).getTime() - new Date(a.signInTime).getTime()
    );

  return sortedSessions.length > 0 ? sortedSessions[0] : null;
};

/**
 * A resource placed on the org chart, shaped to sit in the same table as
 * incident members.
 *
 * `userId` carries the position id so it can serve as the table's key field
 * alongside member rows.
 */
type ResourceRosterRow = {
  userId: string;
  emailAddress: string;
  isResourceRow: true;
  position: IncidentPosition;
};

/**
 * A resource attached to an existing seat. It gets its own row rather than
 * being folded into the seat holder's row, because a seat can hold several
 * people, and a seat with an attached resource may hold nobody at all.
 */
type SeatResourceRosterRow = {
  userId: string;
  emailAddress: string;
  isSeatResourceRow: true;
  seatResource: SeatResource;
  positionName: string | null;
};

type RosterTableRow = RosterEntry | ResourceRosterRow | SeatResourceRosterRow;

const isResourceRow = (row: RosterTableRow): row is ResourceRosterRow =>
  (row as ResourceRosterRow).isResourceRow === true;

const isSeatResourceRow = (row: RosterTableRow): row is SeatResourceRosterRow =>
  (row as SeatResourceRosterRow).isSeatResourceRow === true;

/**
 * The status columns only mean something for a person, so both kinds of
 * resource row fall back to a dash.
 */
const isPersonnelRow = (row: RosterTableRow): row is RosterEntry =>
  !isResourceRow(row) && !isSeatResourceRow(row);

const getLatestLogoutFromWorkLogs = (
  userGuid: string,
  incidentLogs?: IncidentLogEntry[]
): string | null => {
  if (!incidentLogs) return null;
  const userLog = incidentLogs.find((log) => log.userGuid === userGuid);
  if (!userLog?.logs?.length) return null;
  const withLogout = userLog.logs
    .filter((l) => l.logoutTime)
    .sort(
      (a, b) =>
        new Date(b.logoutTime!).getTime() - new Date(a.logoutTime!).getTime()
    );
  return withLogout[0]?.logoutTime ?? null;
};

const RosterTable: React.FC<RosterTableProps> = ({
  rosterData,
  isRosterLoading,
  editMemberButtonClicked,
  removeMemberButtonClicked,
  removeSelfFromIncidentButtonClicked,
  onDeleteSchedule,
  onToggleButtonClick,
  simplified = false,
  currentUserHasApprovalAuthority,
  onToggleApprovalAuthority,
  isTogglingAuthority,
  showApprovalAuthority = true,
  visiblePositionIds = null,
  memberSearch = '',
}) => {
  const { data: incidentLogs } = useGetIncidentLogs();
  const { data: positions = [] } = useGetPositionsForIncident();
  const { data: seatResources = [] } = useGetSeatResourcesForRoster(false);
  const hwcg = useHwcgFlag();
  const { data: activationLog } = useHwcgActivationLog(undefined, {
    enabled: hwcg,
  });
  const activationByUser = useMemo(
    () => indexUsersByGuid(hwcg ? activationLog?.users : undefined),
    [hwcg, activationLog]
  );
  const [openResourcePosition, setOpenResourcePosition] =
    useState<IncidentPosition | null>(null);

  const resourceRows = useMemo<ResourceRosterRow[]>(
    () =>
      positions
        .filter((position) => position.isResourcePosition)
        .map((position) => ({
          userId: position.id,
          emailAddress: position.name,
          isResourceRow: true as const,
          position,
        })),
    [positions]
  );

  const seatResourceRows = useMemo<SeatResourceRosterRow[]>(
    () =>
      seatResources.map((seatResource) => ({
        userId: seatResource.id,
        emailAddress: seatResource.resourceName ?? 'Resource',
        isSeatResourceRow: true as const,
        seatResource,
        positionName:
          positions.find((position) => position.id === seatResource.positionId)
            ?.name ?? null,
      })),
    [seatResources, positions]
  );

  const rows = useMemo<RosterTableRow[]>(() => {
    const all = [...(rosterData || []), ...resourceRows, ...seatResourceRows];
    const term = memberSearch.trim().toLowerCase();
    return all.filter((row) => {
      if (visiblePositionIds) {
        if (isResourceRow(row) && !visiblePositionIds.has(row.position.id)) {
          return false;
        }
        if (
          isSeatResourceRow(row) &&
          !visiblePositionIds.has(row.seatResource.positionId)
        ) {
          return false;
        }
        if (
          isPersonnelRow(row) &&
          !(row.incidentPositions ?? []).some((seat) =>
            visiblePositionIds.has(seat.positionId)
          )
        ) {
          return false;
        }
      }
      if (!term) return true;
      if (isPersonnelRow(row)) return rosterEntryMatchesSearch(row, term);
      if (isResourceRow(row)) {
        return row.position.name.toLowerCase().includes(term);
      }
      return (
        (row.seatResource.resourceName ?? '').toLowerCase().includes(term) ||
        (row.positionName ?? '').toLowerCase().includes(term)
      );
    });
  }, [
    rosterData,
    resourceRows,
    seatResourceRows,
    visiblePositionIds,
    memberSearch,
  ]);

  const firstPersonnelUserId = useMemo(() => {
    const first = rows.find((row) => isPersonnelRow(row));
    return first && isPersonnelRow(first) ? first.userId : null;
  }, [rows]);

  // Mark the first expandable chevron for FOC 7.2 tour targeting.
  useEffect(() => {
    if (!firstPersonnelUserId) return;
    const btn = document.querySelector(
      `[data-testid="legacy-pratus-expander-${firstPersonnelUserId}"]`
    );
    if (btn instanceof HTMLElement) {
      btn.setAttribute('data-tour', 'roster-member-expand');
    }
  }, [firstPersonnelUserId, rows]);

  // Rows are people rather than seats, so a member's category has to be looked up per seat they hold.
  const categoryLabelByPositionId = useMemo(
    () =>
      new Map(
        positions.map((position) => [
          position.id,
          resolveCategoryLabel(position.category, position.custom_category),
        ])
      ),
    [positions]
  );

  const columns = useMemo(() => {
    const memberAndSeatColumns = [
      {
        dataField: 'emailAddress',
        text: 'Incident Member',
        sort: true,
        headerAttrs: { title: 'Sort By:' },
        attrs: { title: 'Incident Member' },
        formatter: (cell: string | null, row: RosterTableRow) => {
          if (isResourceRow(row)) {
            return (
              <button
                type="button"
                className="border-0 bg-transparent p-0 text-start"
                onClick={() => setOpenResourcePosition(row.position)}
              >
                {row.position.name}
              </button>
            );
          }
          const content = cell || '-';
          if (isPersonnelRow(row) && row.userId === firstPersonnelUserId) {
            return <span data-tour="roster-member-summary">{content}</span>;
          }
          return content;
        },
      },
      {
        dataField: 'incidentSeats',
        text: 'Incident Seats',
        sort: true,
        attrs: { title: 'Incident Seats' },
        formatter: (_cell: undefined, row: RosterTableRow) => {
          if (isResourceRow(row)) {
            return row.position.parent_name
              ? `Reports to ${row.position.parent_name}`
              : '-';
          }
          if (isSeatResourceRow(row)) {
            return row.positionName ? `Attached to ${row.positionName}` : '-';
          }
          return (
            row.incidentPositions.map((pos) => pos.positionName).join(', ') ||
            '-'
          );
        },
      },
    ];

    if (simplified) return memberAndSeatColumns;

    return [
      ...memberAndSeatColumns,
      {
        dataField: 'rosterEntryType',
        text: 'Type',
        sort: true,
        attrs: { title: 'Type' },
        formatter: (_cell: undefined, row: RosterTableRow) => {
          if (isResourceRow(row)) {
            return (
              <Badge
                variant="secondary"
                className="tw:h-5 tw:px-1.5 tw:text-[10px]"
              >
                {row.position.resourceType ?? 'Resource'}
              </Badge>
            );
          }
          if (isSeatResourceRow(row)) {
            return (
              <Badge
                variant="secondary"
                className="tw:h-5 tw:px-1.5 tw:text-[10px]"
              >
                {row.seatResource.resourceKind ?? 'Resource'}
              </Badge>
            );
          }
          return <span className="text-muted">Personnel</span>;
        },
      },
      {
        dataField: 'positionCategory',
        text: 'Category',
        sort: true,
        attrs: { title: 'Category' },
        formatter: (_cell: undefined, row: RosterTableRow) => {
          if (isResourceRow(row)) {
            return categoryLabelByPositionId.get(row.position.id) ?? '-';
          }
          if (isSeatResourceRow(row)) {
            return (
              categoryLabelByPositionId.get(row.seatResource.positionId) ?? '-'
            );
          }
          // A member can sit in several seats, each with its own category, so they are all listed the way the
          // Incident Seats column lists the seats themselves.
          const labels = [
            ...new Set(
              row.incidentPositions
                ?.map((position) =>
                  categoryLabelByPositionId.get(position.positionId)
                )
                .filter((label): label is string => !!label) ?? []
            ),
          ];
          return labels.length > 0 ? labels.join(', ') : '-';
        },
      },
      {
        dataField: 'activation_status',
        text: 'Activation Status',
        sort: true,
        attrs: { title: 'Activation Status' },
        formatter: (_cell: undefined, row: RosterTableRow) => {
          if (!isPersonnelRow(row)) return '-';
          const tourHost = row.userId === firstPersonnelUserId;
          let statusNode: React.ReactNode;
          let timestampIso: string | undefined;
          if (row.activatedTimestamp) {
            statusNode = <span className="text-success">Activated</span>;
            timestampIso = row.activatedTimestamp.toString();
          } else if (row.declinedTimestamp) {
            statusNode = <span className="text-danger">Declined</span>;
            timestampIso = row.declinedTimestamp.toString();
          } else {
            statusNode = <span className="text-warning">Pending</span>;
            timestampIso = undefined;
          }
          const notice = hwcg
            ? latestActivationState(activationByUser[row.userId])
            : null;
          const content = (
            <div>
              <StatusWithSubline
                status={statusNode}
                timestampIso={timestampIso}
              />
              {notice ? (
                <small
                  className="text-muted d-block"
                  data-testid={`hwcg-activation-notice-${row.userId}`}
                >
                  Notice: {notice.label}
                  {notice.at ? ` · ${formatLogTime(notice.at)}` : ''}
                </small>
              ) : null}
            </div>
          );
          return tourHost ? (
            <div data-tour="roster-activation-status">{content}</div>
          ) : (
            content
          );
        },
      },
      {
        dataField: 'checkin_status',
        text: 'Check-In Status',
        sort: true,
        attrs: { title: 'Check-In Status' },
        formatter: (_cell: undefined, row: RosterTableRow) => {
          if (!isPersonnelRow(row)) return '-';
          const tourHost = row.userId === firstPersonnelUserId;
          const content =
            row.active && row.activeTime ? (
              <StatusWithSubline
                status={<span className="text-success">Checked In</span>}
                timestampIso={row.activeTime.toString()}
              />
            ) : (
              <StatusWithSubline
                status={<span className="text-muted">Checked Out</span>}
                timestampIso={getLatestLogoutFromWorkLogs(
                  row.userId,
                  incidentLogs
                )}
              />
            );
          return tourHost ? (
            <div data-tour="roster-checkin-status">{content}</div>
          ) : (
            content
          );
        },
      },
      {
        dataField: 'activationNote',
        text: 'Note',
        sort: true,
        attrs: { title: 'Activation Note' },
        formatter: (cell: string | null | undefined) => {
          if (cell && cell.length > 30) {
            return <span title={cell}>{cell.substring(0, 30)}...</span>;
          }
          return cell || '-';
        },
      },
      ...(showApprovalAuthority
        ? [
            {
              dataField: 'approval_authority',
              text: 'Approval Authority',
              sort: true,
              attrs: { title: 'Approval Authority' },
              formatter: (_cell: undefined, row: RosterTableRow) => {
                if (!isPersonnelRow(row)) return '-';
                return <span>{row.approvalAuthority ? 'Yes' : 'No'}</span>;
              },
            },
          ]
        : []),
      {
        dataField: 'signin_status',
        text: 'Sign-In Status (Daily)',
        sort: true,
        attrs: { title: 'Sign-In Status (Daily)' },
        formatter: (_cell: undefined, row: RosterTableRow) => {
          if (!isPersonnelRow(row)) return '-';
          const tourHost = row.userId === firstPersonnelUserId;
          const latestSession = getLatestActiveSession(
            row.userId,
            incidentLogs
          );
          const isSignedIn =
            latestSession?.signInTime && !latestSession.signOutTime;
          const dailyTimestampIso = latestSession
            ? isSignedIn
              ? latestSession.signInTime
              : (latestSession.signOutTime ?? undefined)
            : undefined;
          const content = isSignedIn ? (
            <StatusWithSubline
              status={<span className="text-success">Signed In</span>}
              timestampIso={dailyTimestampIso}
            />
          ) : (
            <StatusWithSubline
              status={<span className="text-muted">Signed Out</span>}
              timestampIso={dailyTimestampIso}
            />
          );
          return tourHost ? (
            <div data-tour="roster-signin-status">{content}</div>
          ) : (
            content
          );
        },
      },
    ];
  }, [
    activationByUser,
    categoryLabelByPositionId,
    firstPersonnelUserId,
    hwcg,
    incidentLogs,
    showApprovalAuthority,
    simplified,
  ]);
  const expandRow = {
    parentClassName: 'parent-expand-tr',
    className: 'child-expand-tr',
    // Resource rows have no member detail to expand into. Standalone ones open the resource modal instead; seat-attached
    // ones are edited from the seat's own modal in the org chart.
    nonExpandable: [...resourceRows, ...seatResourceRows].map(
      (row) => row.userId
    ),
    renderer: (row: RosterEntry) => (
      <RosterExpandedRow
        row={row}
        onDeleteSchedule={onDeleteSchedule}
        onToggleButtonClick={onToggleButtonClick}
        isRosterLoading={isRosterLoading}
        rosterData={rosterData}
        removeMemberButtonClicked={removeMemberButtonClicked}
        removeSelfFromIncidentButtonClicked={
          removeSelfFromIncidentButtonClicked
        }
        editMemberButtonClicked={editMemberButtonClicked}
        toggleDataCheckIn={TOGGLE_DATA_CHECK_IN}
        toggleDataSignIn={TOGGLE_DATA_SIGN_IN}
        simplified={simplified}
        currentUserHasApprovalAuthority={currentUserHasApprovalAuthority}
        onToggleApprovalAuthority={onToggleApprovalAuthority}
        isTogglingAuthority={isTogglingAuthority}
        showApprovalAuthority={showApprovalAuthority}
        tourHost={row.userId === firstPersonnelUserId}
      />
    ),
    showExpandColumn: true,
  };

  return (
    <>
      {hwcg && activationLog && !simplified ? (
        <div className="dark tw:mb-3 tw:rounded-md tw:border tw:border-neutral-700 tw:p-3">
          <HwcgActivationSummary
            summary={activationLog.summary}
            title="Activation notices"
          />
        </div>
      ) : null}
      <div
        className="dark tw:overflow-hidden tw:rounded-md tw:border tw:border-neutral-700"
        data-tour="incident-roster-table"
      >
        <LegacyPratusTable
          keyField={'userId'}
          columns={columns}
          rows={rows}
          expandRow={expandRow}
        />
      </div>

      {openResourcePosition && (
        <RosterResourceModal
          show={true}
          position={openResourcePosition}
          onClose={() => setOpenResourcePosition(null)}
        />
      )}
    </>
  );
};

export default RosterTable;
